import { ChallengeLocationSummary, ChallengeType, GetPointResponse, MapDrawer, TopLevelMarkerHolder } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { delay, isDefined } from '../helpers/utils';

import { StateController } from '../helpers/state/state-management/state-controller';
import challengeData from '../helpers/state/challenge-data';
import { detectMobileViewport } from '../helpers/device';
import topicData from '../helpers/state/topic-data';

/**
 *
 * This component controls the state of whether a point card is in mini-form or full form
 * All expensive calls needed to render both forms of point card should be made here and
 * passed into both components so they arent made twice.
 * Right now, we are doing this with images
 *
 */
@customElement('point-card-wrapper')
export class PointCardWrapper extends LitElement {
    static amplitude_callsite: 'Point Card Wrapper';

    @property()
    point?: GetPointResponse;

    @property()
    challengeLocation?: ChallengeLocationSummary;

    @property({ type: Boolean })
    discoveryView: boolean = false; // when cards are on discovery page, we use a transparent background, only show one photo

    @property({ type: Object })
    associatedMarker?: TopLevelMarkerHolder;

    @property({ type: Boolean })
    isSelected: boolean = false;

    @property({ type: Boolean })
    expandAll: boolean = false;

    @state()
    showFullCard: boolean = false;

    @state()
    previewMode: boolean = false;

    @state()
    hintShowOpen: boolean = false;

    @state()
    hintShowClose: boolean = false;

    @state()
    hasScrollRight: boolean = false;

    @state()
    hasScrollLeft: boolean = false;

    @state()
    multiSelectionMode: boolean = false;

    @state()
    mobilePreview: boolean = false;

    private readonly bindTopicState = new StateController(this, topicData);

    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('isSelected')) {
            if (detectMobileViewport()) {
                // If only 1 point is loaded (e.g. tapped a map dot), skip the mini card
                // and go straight to the expanded card
                const paginationSize = topicData.mapComponent?.pointCardPaginationEl?.pointsToRender?.size ?? 0;
                if (this.isSelected && paginationSize <= 1) {
                    this.showFullCard = true;
                } else {
                    this.hintShowOpen = this.isSelected;
                    this.hintShowClose = this.isSelected;
                }
            } else {
                this.showFullCard = this.isSelected;
            }

            if (!this.isSelected) {
                this.hintShowClose = false;
                this.hintShowOpen = false;
                this.showFullCard = false;
            }

            if (!isDefined(this.challengeLocation) && this.isMapViewOfTreasureChallenge) {
                this.challengeLocation = challengeData.challengeLocations.find(
                    (l) => (isDefined(l.point_info) && l.point_info?._id.$oid === this.point?._id.$oid && l.challenge_location_info?.is_active === true && l.gave_up !== true)
                );
            }
        }
    }

    async setChallengeFocus(challengeLocation: ChallengeLocationSummary | undefined) {
        if (this.isMapViewOfTreasureChallenge && isDefined(this.challengeLocation)) {
            
            // open the challenge card on the map
            await topicData.mapComponent?.setMapDrawer(MapDrawer.ChallengeProgress);

            // wait up to 500ms for the drawer to open and for mapComponent.challengePage to be defined
            const start = Date.now();
            while (isDefined(topicData.mapComponent) && !isDefined(topicData.mapComponent.challengePageComponent) && (Date.now() - start) < 2000) {
                await new Promise(resolve => setTimeout(resolve, 50));
            }

            // set the challenge page to show the activated challenge
            const clueId = challengeLocation?.challenge_location_info?._id?.$oid ?? '';
            if (isDefined(topicData.mapComponent) && isDefined(topicData.mapComponent.challengePageComponent) && isDefined(challengeLocation)) {
                await topicData.mapComponent.challengePageComponent.showClueDetails(clueId);
            }
        }
    }

    async setMapFocus(point?: GetPointResponse) {
        if (!point) {
            return;
        }
        // edge case where opening apoint card triggers loading of more points and scroll behavior gets messed up, this should prevent that
        if(topicData.mapComponent?.pointCardPaginationEl) {
            topicData.mapComponent.pointCardPaginationEl.pauseScrollForLoading = true;
        }

        if (this.associatedMarker) {
            this.showFullCard = true;
        }

        if (this.point?._id.$oid === point?._id.$oid) {
            this.showFullCard = true;
        }

        if (topicData.mapComponent) {
            if (detectMobileViewport()) {
                await topicData.mapComponent.directlySetFocusedPoint(point._id.$oid);
            } else {
                await topicData.mapComponent.onClickPoint(point);
            }
        }
        // On desktop, resume scroll loading after a short delay.
        // On mobile, keep it paused until the expanded card is closed (via closeSelectedCardOnMobile)
        // to prevent infinite scroll from loading dozens of points behind the expanded card.
        if (!detectMobileViewport()) {
            await delay(500).then(() => {
                if (topicData.mapComponent?.pointCardPaginationEl) {
                    topicData.mapComponent.pointCardPaginationEl.pauseScrollForLoading = false;
                }
            });
        }
    }

    closeSelectedCardOnMobile() {
        // Resume infinite scroll now that the expanded card is closing
        if (topicData.mapComponent?.pointCardPaginationEl) {
            topicData.mapComponent.pointCardPaginationEl.pauseScrollForLoading = false;
            topicData.mapComponent.pointCardPaginationEl.useInfiniteScroll = true;
        }
        // If only 1 point was rendered (opened from a map dot), close the whole list
        if (detectMobileViewport() && topicData.mapComponent?.pointCardPaginationEl.pointsToRender.size === 1) {
            topicData.mapComponent?.closeListOfPoints();
            return;
        }
        if (detectMobileViewport()) {
            this.showFullCard = false;
            if (this.isSelected) {
                this.hintShowOpen = true;
                this.hintShowClose = false;
            }
        }
    }

    get used_hint() {
        return this.challengeLocation ? challengeData.activated_challenge?.scavenger_hunt_use?.hint_location_ids?.map(id => id.$oid).includes(this.challengeLocation.challenge_location_info?._id?.$oid ?? '') ?? false : false;
    }

    get isMapViewOfTreasureChallenge() {
        return isDefined(topicData.challengeHappeningNow) && isDefined(challengeData.activatedChallengeId) && challengeData.isType(ChallengeType.Treasure);
    }

    private async loadMorePoints(direction: 'next' | 'previous') {
        await topicData.mapComponent?.pointCardPaginationEl?.fetchMorePoints(direction);
    }

    render() {
        if (this.multiSelectionMode) {
            return html`
                <list-point-card
                    .point=${this.point}
                    .multiSelectionMode=${this.multiSelectionMode}
                    >
                </list-point-card>`;
        }
        return html`
            ${this.showFullCard || this.expandAll ?
                html`
                <div class="card-container" >
                    <expanded-point-card
                    .discoveryView=${this.discoveryView}
                    .point=${this.point}
                    .previewMode=${this.previewMode}
                    .mobilePreview=${this.mobilePreview}
                    @close-expanded-point-card=${() => this.closeSelectedCardOnMobile()}
                >
                </expanded-point-card>
                </div>`
            :
                html`
                    <div class="card-container">
                        ${this.isMapViewOfTreasureChallenge && isDefined(this.challengeLocation) && this.challengeLocation.checked_in !== true ? html`
                            <clue-point-card
                                @on-card-click=${async () => await this.setChallengeFocus(this.challengeLocation)}
                                style="width:100%"
                                .challenge_points=${this.challengeLocation?.challenge_location_info?.value}
                                .clueDetails=${this.challengeLocation?.challenge_location_info?.clue_details}
                                .geo_location_eligible=${false}
                                .pillText=${this.used_hint ? 'Hint Used' : undefined}
                            ></clue-point-card>
                            ${detectMobileViewport() ? html`<div class="point-card-hint-open" @click=${async () => await this.setChallengeFocus(this.challengeLocation)}>Tap to See More</div>` : ''}
                        ` : html`
                        <mini-point-card
                            .point=${this.point}
                            @on-card-click= ${async () => await this.setMapFocus(this.point)}
                        >
                        </mini-point-card>
                            ${detectMobileViewport() ? html`<div class="point-card-hint-open" @click=${async () => await this.setMapFocus(this.point)}>Tap to See More</div>` : ''}
                            ${detectMobileViewport() ? html`<div class="point-card-hint-close" @click=${() => topicData.mapComponent?.closeListOfPoints(true)}>Close</div>` : ''}
                        `}
                    </div>
                ${detectMobileViewport() && this.hasScrollRight ? html`<div aria-label="Points To Right" class="scroll-hint-right" @click=${async () => await this.loadMorePoints('next')}>→</div>` : ''}
                ${detectMobileViewport() && this.hasScrollLeft ? html`<div aria-label="Points To Left" class="scroll-hint-left" @click=${async () => await this.loadMorePoints('previous')}>←</div>` : ''}`
            }
        `;
    }

    static get styles() {
        return css`
            :host {
                flex-shrink: 0;
                width: calc(100% - 10px);
            }

            .card-container {
                position: relative;
            }

            .point-card-hint-open {
                position: absolute;
                right: 5px;
                bottom: 5px;
                background-color: rgba(0, 0, 0, 0.4);
                color: white;
                padding: 2px 6px;
                border-radius: 4px;
                font-size: 12px;
                z-index: 10;
            }

            .point-card-hint-close {
                position: absolute;
                right: 5px;
                bottom: 120px;
                background-color: rgba(0, 0, 0, 0.4);
                color: white;
                padding: 2px 6px;
                border-radius: 4px;
                font-size: 12px;
                z-index: 10;
            }

            .scroll-hint-right {
                position: absolute;
                bottom: 55px;
                right: 10px;
                transform: translateY(-50%);
                background-color: rgba(102, 102, 102, 0.8);
                color: white;
                padding: 2px 4px;
                border-radius: 4px;
                font-size: 10px;
                z-index: 10;
            }

            .scroll-hint-left {
                position: absolute;
                bottom: 55px;
                left: 10px;
                transform: translateY(-50%);
                background-color: rgba(102, 102, 102, 0.8);
                color: white;
                padding: 2px 4px;
                border-radius: 4px;
                font-size: 10px;
                z-index: 10;
            }
        `;
    }
}
